<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  card: { type: Object, default: null },
  stageId: { type: Number, default: null },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();
const store = useStore();

const title = ref(props.card?.title || '');
const description = ref(props.card?.description || '');
const rawValue = ref(props.card?.value || 0);
const displayValue = ref(
  rawValue.value > 0
    ? `R$ ${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(rawValue.value)}`
    : ''
);
const contactId = ref(props.card?.contact?.id || null);
const selectedContactData = ref(props.card?.contact || null);
const contactSearch = ref('');
const showContactDropdown = ref(false);
const contactsLoading = ref(true);
const assigneeId = ref(props.card?.assignee?.id || null);
const selectedAssigneeData = ref(props.card?.assignee || null);
const agentSearch = ref('');
const showAgentDropdown = ref(false);

const contacts = useMapGetter('contacts/getContactsList');
const agents = useMapGetter('agents/getAgents');

const isEditing = computed(() => !!props.card);

const selectedContact = computed(() => {
  if (selectedContactData.value) return selectedContactData.value;
  if (!contactId.value) return null;
  return contacts.value.find(c => c.id === contactId.value);
});

const selectedAssignee = computed(() => {
  if (selectedAssigneeData.value) return selectedAssigneeData.value;
  if (!assigneeId.value) return null;
  return agents.value.find(a => a.id === assigneeId.value);
});

const filteredAgents = computed(() => {
  const list = agents.value || [];
  if (!agentSearch.value) return list.slice(0, 20);
  const q = agentSearch.value.toLowerCase();
  return list.filter(a => a.name?.toLowerCase().includes(q)).slice(0, 20);
});

const formatCurrency = cents => {
  if (cents <= 0) return '';
  const numeric = cents / 100;
  return `R$ ${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numeric)}`;
};

const onValueInput = event => {
  const el = event.target;
  const digits = el.value.replace(/\D/g, '');
  const cents = parseInt(digits, 10) || 0;
  const numeric = cents / 100;
  rawValue.value = numeric;
  const formatted = formatCurrency(cents);
  displayValue.value = formatted;
  nextTick(() => {
    el.value = formatted;
  });
};

onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch('contacts/search', {
        search: '',
        page: 1,
        sortAttr: 'name',
      }),
      store.dispatch('agents/get'),
    ]);
  } finally {
    contactsLoading.value = false;
  }
});

const filteredContacts = computed(() => {
  if (!contactSearch.value) return contacts.value.slice(0, 20);
  const q = contactSearch.value.toLowerCase();
  return contacts.value
    .filter(
      c =>
        c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)
    )
    .slice(0, 20);
});

const selectContact = async contact => {
  contactId.value = contact.id;
  selectedContactData.value = contact;
  contactSearch.value = '';
  showContactDropdown.value = false;
  try {
    const { data } = await ContactAPI.getConversations(contact.id);
    const convs = data?.payload || [];
    const assigned = convs.find(c => c.meta?.assignee);
    if (assigned?.meta?.assignee) {
      assigneeId.value = assigned.meta.assignee.id;
      selectedAssigneeData.value = assigned.meta.assignee;
    }
  } catch {
    // silent — agent field stays empty
  }
};

const clearContact = () => {
  contactId.value = null;
  selectedContactData.value = null;
  contactSearch.value = '';
};

const selectAgent = agent => {
  assigneeId.value = agent.id;
  selectedAssigneeData.value = agent;
  agentSearch.value = '';
  showAgentDropdown.value = false;
};

const clearAgent = () => {
  assigneeId.value = null;
  selectedAssigneeData.value = null;
  agentSearch.value = '';
};

const hideAgentDropdown = () => {
  window.setTimeout(() => {
    showAgentDropdown.value = false;
  }, 200);
};

let searchTimer = null;
const hideDropdown = () => {
  searchTimer = window.setTimeout(() => {
    showContactDropdown.value = false;
  }, 200);
};

watch(contactSearch, q => {
  if (searchTimer) clearTimeout(searchTimer);
  store.dispatch('contacts/search', {
    search: q,
    page: 1,
    sortAttr: 'name',
  });
});

const onSubmit = () => {
  if (!title.value.trim()) return;
  const payload = {
    title: title.value,
    description: description.value,
    value: rawValue.value,
    contact_id: contactId.value,
    assignee_id: assigneeId.value,
    funnel_stage_id: props.stageId || props.card?.funnel_stage_id,
  };
  if (props.card?.id) {
    payload.position = props.card.position;
  }
  emit('save', { id: props.card?.id, funnel_card: payload });
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="
        isEditing ? t('CRM.CARD_FORM.EDIT_TITLE') : t('CRM.CARD_FORM.ADD_TITLE')
      "
    />
    <form class="w-full" @submit.prevent="onSubmit">
      <!-- Title -->
      <div class="w-full">
        <label
          class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12 mb-1"
        >
          <span class="i-lucide-type size-4 text-n-slate-10" />
          {{ t('CRM.CARD_FORM.TITLE_LABEL') }}
        </label>
        <input
          v-model="title"
          type="text"
          :placeholder="t('CRM.CARD_FORM.TITLE_PLACEHOLDER')"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-7 transition-colors"
        />
      </div>

      <!-- Description -->
      <div class="w-full mt-3">
        <label
          class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12 mb-1"
        >
          <span class="i-lucide-file-text size-4 text-n-slate-10" />
          {{ t('CRM.CARD_FORM.DESCRIPTION_LABEL') }}
        </label>
        <textarea
          v-model="description"
          rows="3"
          :placeholder="t('CRM.CARD_FORM.DESCRIPTION_PLACEHOLDER')"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-7 transition-colors"
        />
      </div>

      <!-- Value -->
      <div class="w-full mt-3">
        <label
          class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12 mb-1"
        >
          <span class="i-lucide-dollar-sign size-4 text-n-slate-10" />
          {{ t('CRM.CARD_FORM.VALUE_LABEL') }}
        </label>
        <input
          :value="displayValue"
          type="text"
          inputmode="decimal"
          :placeholder="t('CRM.CARD_FORM.VALUE_PLACEHOLDER')"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-7 transition-colors"
          @input="onValueInput"
        />
      </div>

      <!-- Contact -->
      <div class="w-full mt-3">
        <label
          class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12 mb-1"
        >
          <span class="i-lucide-user size-4 text-n-slate-10" />
          {{ t('CRM.CARD_FORM.CONTACT_LABEL') }}
        </label>

        <!-- Selected contact chip -->
        <div
          v-if="selectedContact"
          class="flex items-center gap-2 mb-2 p-2 bg-n-alpha-2 rounded-lg"
        >
          <Avatar
            :name="selectedContact.name"
            :src="selectedContact.thumbnail"
            :size="24"
            rounded-full
          />
          <span class="text-sm text-n-slate-12 flex-1 truncate">
            {{ selectedContact.name }}
          </span>
          <button
            type="button"
            class="size-5 grid place-content-center rounded hover:bg-n-alpha-3 transition-colors"
            @click="clearContact"
          >
            <span class="i-lucide-x size-3.5 text-n-slate-10" />
          </button>
        </div>

        <!-- Contact search -->
        <div v-else class="relative">
          <input
            v-model="contactSearch"
            type="text"
            :placeholder="t('CRM.CARD_FORM.CONTACT_PLACEHOLDER')"
            class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-7 transition-colors"
            @focus="showContactDropdown = true"
            @blur="hideDropdown"
          />
          <div
            v-if="
              showContactDropdown &&
              (filteredContacts.length > 0 || contactsLoading)
            "
            class="absolute z-10 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white dark:bg-n-slate-2 border border-n-weak rounded-lg shadow-lg"
          >
            <div
              v-if="contactsLoading"
              class="px-3 py-4 text-sm text-n-slate-9 text-center"
            >
              <span
                class="i-lucide-loader-2 size-4 animate-spin inline-block mr-1 align-middle"
              />
              {{ t('CRM.CARD_FORM.LOADING_CONTACTS') }}
            </div>
            <button
              v-for="contact in filteredContacts"
              :key="contact.id"
              type="button"
              class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors text-left"
              @mousedown="selectContact(contact)"
            >
              <Avatar
                :name="contact.name"
                :src="contact.thumbnail"
                :size="24"
                rounded-full
              />
              <div class="min-w-0 flex-1">
                <div class="truncate font-medium">{{ contact.name }}</div>
                <div
                  v-if="contact.email"
                  class="truncate text-xs text-n-slate-10"
                >
                  {{ contact.email }}
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Agent -->
      <div class="w-full mt-3">
        <label
          class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12 mb-1"
        >
          <span class="i-lucide-headset size-4 text-n-slate-10" />
          {{ t('CRM.CARD_FORM.AGENT_LABEL') }}
        </label>

        <!-- Selected agent chip -->
        <div
          v-if="selectedAssignee"
          class="flex items-center gap-2 mb-2 p-2 bg-n-alpha-2 rounded-lg"
        >
          <Avatar
            :name="selectedAssignee.name"
            :src="selectedAssignee.thumbnail"
            :size="24"
            rounded-full
          />
          <span class="text-sm text-n-slate-12 flex-1 truncate">
            {{ selectedAssignee.name }}
          </span>
          <button
            type="button"
            class="size-5 grid place-content-center rounded hover:bg-n-alpha-3 transition-colors"
            @click="clearAgent"
          >
            <span class="i-lucide-x size-3.5 text-n-slate-10" />
          </button>
        </div>

        <!-- Agent search -->
        <div v-else class="relative">
          <input
            v-model="agentSearch"
            type="text"
            :placeholder="t('CRM.CARD_FORM.AGENT_PLACEHOLDER')"
            class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-7 transition-colors"
            @focus="showAgentDropdown = true"
            @blur="hideAgentDropdown"
          />
          <div
            v-if="showAgentDropdown && filteredAgents.length > 0"
            class="absolute z-10 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white dark:bg-n-slate-2 border border-n-weak rounded-lg shadow-lg"
          >
            <button
              v-for="agent in filteredAgents"
              :key="agent.id"
              type="button"
              class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors text-left"
              @mousedown="selectAgent(agent)"
            >
              <Avatar
                :name="agent.name"
                :src="agent.thumbnail"
                :size="24"
                rounded-full
              />
              <span class="truncate">{{ agent.name }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-2 py-3 mt-2">
        <Button
          faded
          slate
          type="button"
          :label="t('CRM.CARD_FORM.CANCEL')"
          @click="emit('close')"
        />
        <Button type="submit" :label="t('CRM.CARD_FORM.SAVE')" />
      </div>
    </form>
  </div>
</template>
